import React, { useState } from "react";
import { CheckCircle2, Truck, ShoppingBag, Phone, PackageCheck, ShieldCheck, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import { heroBullets, heroImage, countriesList, serviceModes, medicineTypes, contact } from "../mock";

const QuoteForm = () => {
  const [mode, setMode] = useState("pickup");
  const [country, setCountry] = useState("");
  const [medType, setMedType] = useState("Allopathic Medicines");
  const [mobile, setMobile] = useState("");
  const [prescription, setPrescription] = useState("yes");

  const weight = "0.5 kg";
  const activeMode = serviceModes.find((m) => m.id === mode);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!country) { toast.error("Please select a destination country"); return; }
    if (!mobile || mobile.length < 8) { toast.error("Please enter a valid mobile number"); return; }
    const est = 4400;
    toast.success(`Estimated charge to ${country} (${weight}): ₹${est.toLocaleString()} — our team will WhatsApp you shortly!`);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden card-shadow border border-slate-100 w-full">
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-center py-3.5 px-4">
        <h3 className="text-lg font-bold tracking-wide">CHOOSE SERVICE FROM BELOW OPTION</h3>
      </div>
      <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Select Service Mode *</label>
          <div className="grid grid-cols-2 gap-3">
            {serviceModes.map(({ id, label, Icon }) => {
              const IconComp = { Truck, ShoppingBag }[Icon];
              const active = mode === id;
              return (
                <button
                  type="button"
                  key={id}
                  onClick={() => setMode(id)}
                  aria-pressed={active}
                  className={`relative flex flex-col items-center gap-1.5 py-3.5 px-2 rounded-xl border-2 transition-all ${active ? "border-teal-500 bg-teal-50 text-teal-700 brand-shadow" : "border-slate-200 text-slate-600 hover:border-teal-300 hover:bg-slate-50"}`}
                >
                  {active && <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-teal-500" />}
                  <IconComp className="w-6 h-6" />
                  <span className="text-xs font-semibold text-center leading-tight">{label}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-500 leading-relaxed">
            <PackageCheck className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />
            {activeMode?.desc}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Select Country *</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="">Select Country</option>
              {countriesList.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Weight (KG) <span className="text-red-500">*</span></label>
            <div className="w-full h-11 px-3 rounded-lg border border-teal-200 bg-teal-50 flex items-center justify-between">
              <span className="text-teal-700 font-bold text-base">0.5 kg</span>
              <span className="text-[11px] font-medium text-teal-600 bg-white px-2 py-0.5 rounded-full">Standard</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Medicines Type *</label>
            <select value={medType} onChange={(e) => setMedType(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
              {medicineTypes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Mobile No. *</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-200 bg-slate-50 text-sm text-slate-600">+91</span>
              <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Enter Mobile Number" className="w-full h-11 px-3 rounded-r-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Do you have prescription ?</label>
          <div className="grid grid-cols-2 gap-3">
            {["yes", "no"].map((v) => (
              <button type="button" key={v} onClick={() => setPrescription(v)} className={`py-2.5 rounded-lg border-2 font-semibold text-sm uppercase transition-all ${prescription === v ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-500 hover:border-teal-300"}`}>{v}</button>
            ))}
          </div>
        </div>

        <button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-base py-3.5 rounded-lg transition-colors">
          Check Courier Charges
        </button>
      </form>
    </div>
  );
};

export default function Hero() {
  return (
    <section className="relative bg-gradient-to-b from-teal-50/70 via-white to-white overflow-hidden">
      <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "radial-gradient(circle at 18% 15%, rgba(13,148,136,0.10), transparent 42%), radial-gradient(circle at 85% 5%, rgba(37,99,235,0.07), transparent 42%)" }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-200/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 pt-10 lg:pt-14 pb-8 grid lg:grid-cols-2 gap-8 items-start">
        {/* Left */}
        <div className="relative rounded-3xl overflow-hidden card-shadow fade-up ring-1 ring-black/5">
          <img src={heroImage} alt="Medical courier delivery agent" className="w-full h-[440px] lg:h-[580px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-teal-950/90 via-teal-900/25 to-transparent" />

          {/* Floating guarantee badge */}
          <div className="hidden sm:flex absolute top-5 left-5 items-center gap-2.5 bg-white/95 backdrop-blur rounded-2xl px-4 py-2.5 shadow-lg">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div className="leading-tight">
              <div className="text-[11px] font-bold text-slate-900">100% Customs Clearance</div>
              <div className="text-[10px] text-slate-500">Guaranteed on Ayurvedic &amp; Homeopathy</div>
            </div>
          </div>

          {/* Floating rating chip */}
          <div className="hidden sm:flex absolute top-5 right-5 items-center gap-1.5 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-lg">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-sm font-bold text-slate-900">4.9</span>
            <span className="text-[11px] text-slate-500">/ 5</span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
            <p className="inline-flex items-center gap-2 text-teal-100 font-semibold uppercase tracking-widest text-xs mb-3 bg-white/10 backdrop-blur px-3 py-1 rounded-full">Customer’s First &amp; Trusted Choice</p>
            <h1 className="text-3xl sm:text-[2.6rem] font-extrabold leading-[1.1] mb-5">For International <span className="text-teal-300">Medicine Courier</span> Services</h1>
            <div className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
              {heroBullets.map((b) => (
                <div key={b} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-teal-300 shrink-0 mt-0.5" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Right */}
        <div className="fade-up">
          <QuoteForm />
          <a href={`tel:${contact.phoneRaw}`} className="mt-4 flex items-center justify-center gap-2 text-slate-600 text-sm">
            <Phone className="w-4 h-4 text-teal-600" /> Prefer to talk? Call our experts at <span className="font-semibold text-teal-700">{contact.phone}</span>
          </a>
        </div>
      </div>

      {/* Elegant trust strip */}
      <div className="relative max-w-7xl mx-auto px-4 pb-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            { icon: ShieldCheck, title: "100% Clearance", sub: "Customs guaranteed" },
            { icon: Truck, title: "Free Pickup", sub: "Doorstep across India" },
            { icon: Clock, title: "5-6 Day Delivery", sub: "Priority worldwide" },
            { icon: Star, title: "50,000+ Parcels", sub: "Delivered safely" },
          ].map((t) => (
            <div key={t.title} className="flex items-center gap-3 bg-white rounded-2xl border border-slate-100 card-shadow px-4 py-3.5 hover:-translate-y-0.5 transition-transform">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0"><t.icon className="w-5 h-5" /></div>
              <div className="leading-tight">
                <div className="text-sm font-bold text-slate-900">{t.title}</div>
                <div className="text-xs text-slate-500">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
